import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams as useReactRouterParams } from 'react-router-dom';

// Compatibility shim for `next/navigation`.

export function usePathname(): string {
  return useLocation().pathname;
}

export interface AppRouter {
  push: (href: string) => void;
  replace: (href: string) => void;
  back: () => void;
  forward: () => void;
  refresh: () => void;
  prefetch: (href: string) => void;
}

export function useRouter(): AppRouter {
  const navigate = useNavigate();
  return useMemo<AppRouter>(
    () => ({
      push: (href: string) => navigate(href),
      replace: (href: string) => navigate(href, { replace: true }),
      back: () => navigate(-1),
      forward: () => navigate(1),
      // A SPA has no server render to refresh; reloading the route data is a
      // no-op here because components re-fetch on their own.
      refresh: () => {},
      prefetch: () => {},
    }),
    [navigate],
  );
}

export type ReadonlyURLSearchParams = URLSearchParams;

export function useSearchParams(): URLSearchParams {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export function useParams<T extends Record<string, string | undefined> = Record<string, string>>(): T {
  // Next pages read route params from this hook; React Router supplies them.
  return useReactRouterParams() as T;
}

export function redirect(href: string): never {
  throw new Error(`redirect() is not available in the SPA (${href})`);
}

export function notFound(): never {
  throw new Error('notFound() is not available in the SPA');
}

export function useCallbackStub(): void {
  useCallback(() => {}, []);
}
